import type {
  ClaimRelationshipView,
  RelatedClaimSummary,
} from '@billing/validation';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Collapse,
  Divider,
  Group,
  Loader,
  Modal,
  Pagination,
  Radio,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  useComputedColorScheme,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useTranslation } from '@refinedev/core';
import {
  IconChevronDown,
  IconExternalLink,
  IconLink,
  IconLinkOff,
  IconPlus,
  IconSearch,
} from '@tabler/icons-react';
import {
  useClaimRelationshipCandidates,
  useClaimRelationships,
  useCreateClaimRelationship,
  useDeleteClaimRelationship,
} from 'api/resource';
import { useCanModify } from 'hooks/useCanModify';
import type { Claim } from 'interfaces';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useCurrentSiteStore } from 'store/SiteStore';
import { buttonTextColor, getOutlineControlProps } from 'utils/buttonUtils';
import {
  getClaimRelationshipLabelKey,
  mapClaimRelationshipChoice,
  type ClaimRelationshipChoice,
} from 'pages/claims/utils/claimRelationshipUtils';

const tariffDisplayCode = (
  tariff: RelatedClaimSummary['tariffs'][number]
): string => `${tariff.prefix || ''}${tariff.code}`;

type Translate = ReturnType<typeof useTranslation>['translate'];
type ClaimReferenceContext = 'standalone' | 'inline';

const getClaimReference = (
  claim: Pick<RelatedClaimSummary, 'id' | 'claimNumber'>,
  translate: Translate,
  context: ClaimReferenceContext
): string => {
  if (claim.claimNumber) {
    if (context === 'inline') {
      return translate(
        'pages:claims.relationships.numberedClaimReference',
        { claimNumber: claim.claimNumber },
        `claim #${claim.claimNumber}`
      );
    }

    return translate(
      'pages:claims.relationships.claimNumber',
      { claimNumber: claim.claimNumber },
      `Claim #${claim.claimNumber}`
    );
  }

  const shortClaimID = claim.id.slice(0, 8);
  return context === 'inline'
    ? translate(
        'pages:claims.relationships.claimIDReference',
        { claimID: shortClaimID },
        `claim ${shortClaimID}`
      )
    : translate(
        'pages:claims.relationships.claimID',
        { claimID: shortClaimID },
        `Claim ${shortClaimID}`
      );
};

const CandidateDetails = ({ claim }: { claim: RelatedClaimSummary }) => {
  const { translate } = useTranslation();
  const { timezone } = useCurrentSiteStore();
  const claimReference = getClaimReference(claim, translate, 'standalone');
  const serviceDate = DateTime.fromISO(claim.serviceStart, { setZone: true })
    .setZone(timezone)
    .toLocaleString(DateTime.DATE_MED);

  return (
    <Stack gap={2}>
      <Text fw={600}>{claimReference}</Text>
      <Text size="xs" c="dimmed">
        {[serviceDate, claim.payor?.name].filter(Boolean).join(' · ')}
      </Text>
      {claim.tariffs.length > 0 && (
        <Text size="xs" c="dimmed">
          {claim.tariffs.map(tariffDisplayCode).join(', ')}
        </Text>
      )}
    </Stack>
  );
};

const relationshipChoices: ClaimRelationshipChoice[] = [
  'RELATED',
  'CURRENT_SPLIT_FROM_SELECTED',
  'SELECTED_SPLIT_FROM_CURRENT',
  'CURRENT_COPY_OF_SELECTED',
  'SELECTED_COPY_OF_CURRENT',
];

const AddClaimRelationshipModal = ({
  claim,
  opened,
  onClose,
}: {
  claim: Claim;
  opened: boolean;
  onClose: () => void;
}) => {
  const { translate } = useTranslation();
  const computedColorScheme = useComputedColorScheme('light');
  const [choice, setChoice] = useState<ClaimRelationshipChoice>('RELATED');
  const [selectedClaimID, setSelectedClaimID] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [debouncedSearch] = useDebouncedValue(search.trim(), 300);
  const candidates = useClaimRelationshipCandidates({
    claimID: claim.id,
    search: debouncedSearch,
    page,
    enabled: opened && Boolean(claim.patientID),
  });
  const createRelationship = useCreateClaimRelationship();
  const relationshipOptions = relationshipChoices.map((relationshipChoice) => ({
    value: relationshipChoice,
    label: translate(
      `pages:claims.relationships.choices.${relationshipChoice}`
    ),
  }));
  const linkDisabled = !selectedClaimID || !claim.patientID;

  const handleClose = () => {
    setSelectedClaimID('');
    setSearch('');
    setPage(0);
    onClose();
  };

  const handleCreate = async () => {
    if (!selectedClaimID) return;
    await createRelationship.mutateAsync(
      mapClaimRelationshipChoice({
        currentClaimID: claim.id,
        selectedClaimID,
        choice,
      })
    );
    handleClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Text size="lg" fw={700}>
          {translate(
            'pages:claims.relationships.addModal.title',
            'Link Claims'
          )}
        </Text>
      }
      size="lg"
    >
      <Stack gap="md">
        {!claim.patientID && (
          <Alert color="yellow">
            {translate(
              'pages:claims.relationships.savedPatientRequired',
              'Save a patient on this claim before linking it to another claim.'
            )}
          </Alert>
        )}
        <Select
          value={choice}
          onChange={(value) => {
            const nextChoice = relationshipChoices.find(
              (relationshipChoice) => relationshipChoice === value
            );
            if (nextChoice) setChoice(nextChoice);
          }}
          label={translate(
            'pages:claims.relationships.addModal.relationshipLabel',
            'How are these claims related?'
          )}
          data={relationshipOptions}
          allowDeselect={false}
          radius="md"
          size="md"
          styles={{
            label: {
              fontSize: 'var(--mantine-font-size-sm)',
              fontWeight: 600,
            },
          }}
        />
        <Divider />
        <Stack gap="sm">
          <Text fw={600} size="sm">
            {translate(
              'pages:claims.relationships.addModal.claimLabel',
              'Select a claim'
            )}
          </Text>
          <TextInput
            value={search}
            onChange={(event) => {
              setSearch(event.currentTarget.value);
              setPage(0);
            }}
            aria-label={translate(
              'pages:claims.relationships.addModal.searchLabel',
              'Find a claim'
            )}
            placeholder={translate(
              'pages:claims.relationships.addModal.searchPlaceholder',
              'Search claim number, payor, or tariff'
            )}
            leftSection={<IconSearch size={16} />}
            disabled={!claim.patientID}
            radius="md"
            size="md"
          />
          {candidates.isLoading && <Loader size="sm" />}
          {candidates.isError && (
            <Alert color="red">
              {translate(
                'pages:claims.relationships.candidatesError',
                'Could not load claims available to link.'
              )}
            </Alert>
          )}
          {candidates.data?.data.length === 0 && (
            <Text size="sm" c="dimmed">
              {translate(
                'pages:claims.relationships.noCandidates',
                'No unlinked claims were found for this patient and site.'
              )}
            </Text>
          )}
          {candidates.data && candidates.data.data.length > 0 && (
            <Radio.Group value={selectedClaimID} onChange={setSelectedClaimID}>
              <ScrollArea.Autosize mah={280}>
                <Stack gap="xs">
                  {candidates.data.data.map((candidate) => (
                    <Card
                      key={candidate.id}
                      withBorder
                      padding="sm"
                      radius="md"
                    >
                      <Radio
                        value={candidate.id}
                        label={<CandidateDetails claim={candidate} />}
                      />
                    </Card>
                  ))}
                </Stack>
              </ScrollArea.Autosize>
            </Radio.Group>
          )}
          {(candidates.data?.pages ?? 0) > 1 && (
            <Pagination
              value={page + 1}
              total={candidates.data?.pages ?? 1}
              onChange={(nextPage) => setPage(nextPage - 1)}
              size="sm"
            />
          )}
        </Stack>
        <Group justify="flex-end">
          <Button
            variant="outline"
            radius="md"
            color="gray"
            c={buttonTextColor(computedColorScheme, false)}
            onClick={handleClose}
          >
            {translate('common:buttons.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleCreate}
            disabled={linkDisabled}
            loading={createRelationship.isPending}
            leftSection={<IconLink size={16} />}
            radius="md"
            {...getOutlineControlProps(computedColorScheme, linkDisabled)}
          >
            {translate(
              'pages:claims.relationships.addModal.linkAction',
              'Link claims'
            )}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

const RelationshipCard = ({
  relationship,
  timezone,
  canModify,
}: {
  relationship: ClaimRelationshipView;
  timezone: string;
  canModify: boolean;
}) => {
  const { translate } = useTranslation();
  const computedColorScheme = useComputedColorScheme('light');
  const deleteRelationship = useDeleteClaimRelationship();
  const labelKey = getClaimRelationshipLabelKey(
    relationship.relationshipType,
    relationship.currentClaimSide
  );
  const serviceDate = DateTime.fromISO(relationship.relatedClaim.serviceStart, {
    setZone: true,
  })
    .setZone(timezone)
    .toLocaleString(DateTime.DATE_MED);
  const claimReference = getClaimReference(
    relationship.relatedClaim,
    translate,
    'inline'
  );
  const relationshipLabel = translate(
    `pages:claims.relationships.labels.${labelKey}`
  );
  const heading = translate(
    `pages:claims.relationships.headings.${labelKey}`,
    { claim: claimReference },
    `${relationshipLabel} ${claimReference}`
  );
  const handleUnlink = () => {
    const confirmed = window.confirm(
      translate(
        'pages:claims.relationships.unlinkConfirmation',
        'Unlink these claims?'
      )
    );
    if (!confirmed) return;
    deleteRelationship.mutate({
      relationshipID: relationship.id,
      fromClaimID: relationship.fromClaimID,
      toClaimID: relationship.toClaimID,
    });
  };

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Text fw={700} size="lg">
            {heading}
          </Text>
          <Group gap="sm">
            <Button
              component="a"
              href={`/claims/edit/${relationship.relatedClaim.id}`}
              target="_blank"
              rel="noopener noreferrer"
              radius="md"
              leftSection={<IconExternalLink size={16} />}
              {...getOutlineControlProps(computedColorScheme, false)}
            >
              {translate('pages:claims.relationships.open', 'Open')}
            </Button>
            {canModify && (
              <Button
                radius="md"
                onClick={handleUnlink}
                loading={deleteRelationship.isPending}
                leftSection={<IconLinkOff size={16} />}
                {...getOutlineControlProps(
                  computedColorScheme,
                  false,
                  { color: buttonTextColor(computedColorScheme, false) },
                  'danger'
                )}
              >
                {translate('pages:claims.relationships.unlink', 'Unlink')}
              </Button>
            )}
          </Group>
        </Group>
        <Divider />
        <Group gap="xl" align="flex-start">
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              {translate(
                'pages:claims.relationships.serviceDate',
                'Service date'
              )}
            </Text>
            <Text size="sm" fw={600}>
              {serviceDate}
            </Text>
          </Stack>
          {relationship.relatedClaim.payor?.name && (
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                {translate('pages:claims.relationships.payor', 'Payor')}
              </Text>
              <Text size="sm" fw={600}>
                {relationship.relatedClaim.payor.name}
              </Text>
            </Stack>
          )}
          {relationship.relatedClaim.tariffs.length > 0 && (
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                {translate('pages:claims.relationships.tariffs', 'Tariffs')}
              </Text>
              <Group gap="xs">
                {relationship.relatedClaim.tariffs.map((tariff) => (
                  <Badge key={tariff.id} color="gray" variant="outline">
                    {tariffDisplayCode(tariff)}
                  </Badge>
                ))}
              </Group>
            </Stack>
          )}
        </Group>
      </Stack>
    </Card>
  );
};

export const RelatedClaimsPanel = ({ claim }: { claim: Claim }) => {
  const { translate } = useTranslation();
  const computedColorScheme = useComputedColorScheme('light');
  const { timezone } = useCurrentSiteStore();
  const { canModify } = useCanModify('claims');
  const [addModalOpened, setAddModalOpened] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const relationships = useClaimRelationships(claim.id);
  const toggleAriaLabel = expanded
    ? translate(
        'pages:claims.relationships.collapse',
        'Collapse related claims'
      )
    : translate('pages:claims.relationships.expand', 'Expand related claims');

  return (
    <Card shadow="lg" radius="sm" padding="lg" withBorder mt="md">
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="xs">
            <IconLink size={20} />
            <Text fw={700}>
              {translate('pages:claims.relationships.title', 'Related Claims')}
            </Text>
            {(relationships.data?.length ?? 0) > 0 && (
              <Badge variant="light">{relationships.data?.length}</Badge>
            )}
          </Group>
          <Group gap="xs">
            {canModify && (
              <Button
                variant="outline"
                size="sm"
                radius="md"
                color="gray"
                c={buttonTextColor(computedColorScheme, !claim.patientID)}
                onClick={() => setAddModalOpened(true)}
                leftSection={<IconPlus size={16} />}
                disabled={!claim.patientID}
              >
                {translate('pages:claims.relationships.add', 'Link Claim')}
              </Button>
            )}
            <ActionIcon
              size="input-sm"
              variant="subtle"
              color="gray"
              radius="sm"
              aria-label={toggleAriaLabel}
              aria-expanded={expanded}
              aria-controls="related-claims-content"
              onClick={() => setExpanded((value) => !value)}
            >
              <IconChevronDown
                size={18}
                style={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 150ms ease',
                }}
              />
            </ActionIcon>
          </Group>
        </Group>
        <Collapse id="related-claims-content" in={expanded}>
          <Stack gap="md">
            {relationships.isLoading && <Loader size="sm" />}
            {relationships.isError && (
              <Alert color="red">
                {translate(
                  'pages:claims.relationships.loadError',
                  'Could not load related claims.'
                )}
              </Alert>
            )}
            {relationships.data?.length === 0 && (
              <Text size="sm" c="dimmed">
                {claim.patientID
                  ? translate(
                      'pages:claims.relationships.empty',
                      'This claim is not linked to any other claims.'
                    )
                  : translate(
                      'pages:claims.relationships.savedPatientRequired',
                      'Save a patient on this claim before linking it to another claim.'
                    )}
              </Text>
            )}
            {relationships.data?.map((relationship) => (
              <RelationshipCard
                key={relationship.id}
                relationship={relationship}
                timezone={timezone}
                canModify={canModify}
              />
            ))}
          </Stack>
        </Collapse>
      </Stack>
      <AddClaimRelationshipModal
        claim={claim}
        opened={addModalOpened}
        onClose={() => setAddModalOpened(false)}
      />
    </Card>
  );
};
